import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { updateAiModelColor } from "./aiModels.thunk";
// Served from `public/svg/` (not imported from `src/assets/`) so Vite never
// runs them through the asset pipeline: even a `?url` import gets inlined as
// a `data:` URI for a file this small in dev, and the app's `resolveLogoUrl`
// allowlist rejects `data:` URIs, silently falling back to the initials
// chip. A `public/` path is always served byte-for-byte, in dev and build.
const CHATGPT_LOGO = "/svg/aiAssistants/chatgpt.svg";
const GEMINI_LOGO = "/svg/aiAssistants/gemini.svg";
const CLAUDE_LOGO = "/svg/aiAssistants/claude.svg";

// Logos live on the frontend for ChatGPT / Gemini / Claude until the API's
// logo URLs are ready. Everything else (id / name / displayName / color)
// comes from bootstrap; `setAiModels` overlays only the logo.
const LOGO_BY_ID: Record<number, string> = {
  5001: CHATGPT_LOGO,
  5002: GEMINI_LOGO,
  5003: CLAUDE_LOGO,
};

function normalizeModelName(value?: string): string {
  return (value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

function resolveModelLogo(model: {
  id: number;
  name?: string;
  displayName?: string;
}): string {
  const byId = LOGO_BY_ID[model.id];
  if (byId) return byId;
  const name =
    normalizeModelName(model.displayName) || normalizeModelName(model.name);
  if (name.includes("chatgpt") || name.startsWith("gpt") || name.includes("openai")) {
    return CHATGPT_LOGO;
  }
  if (name.includes("gemini")) return GEMINI_LOGO;
  if (name.includes("claude") || name.includes("anthropic")) return CLAUDE_LOGO;
  return "";
}

/**
 * An AI assistant (ChatGPT, Gemini, Claude) shaped like a `Brand` just
 * enough to flow through the same generic entity pipeline (`entities[key]`
 * lookups in the response formatters, `EntityIcon`/`getIconForEntity` for
 * the logo, `CardHeader`/gauge for the color) that brands already use.
 * `id` is the integer model id the API expects on `models: number[]`
 * (mirrors `Brand.id`).
 */
export interface AiModel {
  id: number;
  name: string;
  displayName: string;
  logo: string;
  color: string;
  type: "ASSISTANT";
}

export interface AiModelsState {
  ids: number[];
  entities: Record<number, AiModel>;
}

/**
 * Shape of a bootstrap `aiModels` entry. The wire `logo` is a placeholder
 * today and is deliberately not consumed (see `LOGO_BY_ID`).
 */
export type AiModelDto = Omit<AiModel, "logo" | "type" | "color"> & {
  logo?: string;
  color?: string;
  type?: "ASSISTANT";
};

const initialState: AiModelsState = {
  ids: [],
  entities: {},
};

const aiModelsSlice = createSlice({
  name: "aiModels",
  initialState,
  reducers: {
    reorderAiModels: (state, action: PayloadAction<number[]>) => {
      const incoming = action.payload.filter((id) => state.entities[id]);
      const remaining = state.ids.filter((id) => !incoming.includes(id));
      state.ids = [...incoming, ...remaining];
    },
    /**
     * Sourced solely from the unified bootstrap API (`clients[0].aiModels`);
     * there is no frontend default. A null/empty payload clears the catalog.
     * Entries without a valid numeric `id` are dropped so `null`/`NaN` ids
     * never reach dropdown values, `models: number[]` pruning or the payload.
     */
    setAiModels: (state, action: PayloadAction<AiModelDto[] | null>) => {
      const models = (action.payload ?? []).filter(
        (model) => typeof model.id === "number" && Number.isFinite(model.id),
      );
      state.ids = models.map((model) => model.id);
      state.entities = Object.fromEntries(
        models.map((model): [number, AiModel] => {
          const { logo: _logo, color, ...rest } = model;
          return [
            model.id,
            {
              ...rest,
              type: "ASSISTANT",
              logo: resolveModelLogo(model),
              color: color ?? "",
            },
          ];
        }),
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateAiModelColor.fulfilled, (state, action) => {
      if (action.payload !== null) {
        const model = state.entities[action.payload.modelId];
        if (model) model.color = action.payload.color;
      }
    });
  },
});

export const { setAiModels, reorderAiModels } = aiModelsSlice.actions;
export default aiModelsSlice.reducer;
